import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, ViewEncapsulation, OnInit, AfterViewInit } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule, AbstractControl, ValidationErrors } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { UserService } from '../../../../services/user.service';
import { emailValidator } from '../../../../common/validators/form.validator';
import { ToastrService } from 'ngx-toastr';

const PASSWORD_MIN_LENGTH = 6;

@Component({
  selector: 'wire-reset',
  templateUrl: './reset.component.html',
  styleUrls: ['./reset.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, RouterModule],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetComponent implements AfterViewInit {
  private readonly userService = inject(UserService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly route = inject(ActivatedRoute);
  private readonly toastr = inject(ToastrService);

  emailSent = false;
  emailClicked = false;
  resetPassword = false;
  resetSuccess = false;
  isLoading = false;

  readonly resetForm = new FormGroup<{
    email: FormControl<string | null>;
  }>({
    email: new FormControl<string | null>(null, {
      validators: [Validators.required, emailValidator],
      nonNullable: false
    })
  });

  readonly passwordResetForm = new FormGroup<{
    password: FormControl<string | null>;
    confirmPassword: FormControl<string | null>;
  }>({
    password: new FormControl<string | null>(null, {
      validators: [Validators.required, Validators.minLength(PASSWORD_MIN_LENGTH)],
      nonNullable: false
    }),
    confirmPassword: new FormControl<string | null>(null, {
      validators: [Validators.required, Validators.minLength(PASSWORD_MIN_LENGTH)],
      nonNullable: false
    })
  }, {
    validators: this.passwordMatchValidator
  });

  get emailDomain(): string | undefined {
    return this.resetForm.get('email')?.value?.split('@')[1];
  }

  get passwordMinLength(): number {
    return PASSWORD_MIN_LENGTH;
  }

  ngAfterViewInit(): void {
    const resetCode = this.route.snapshot.queryParams['code'];
    if (resetCode) {
      this.resetPassword = true;
      this.emailClicked = true;
      this.emailSent = false;
      this.cdr.detectChanges();
    }
  }

  goToDomain(): void {
    if (this.emailDomain) {
      window.open(`https://${this.emailDomain}`, '_blank');
    }
  }

  forgotPassword(): void {
    if (this.resetForm.invalid || this.isLoading) return;

    this.isLoading = true;
    const email = this.resetForm.get('email')?.value;
    
    if (!email) {
      this.toastr.error('Email is required');
      this.isLoading = false;
      return;
    }

    this.userService.forgotPassword(email).subscribe({
      next: () => {
        this.emailSent = true;
        this.resetPassword = true;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.toastr.error(error.error.message || 'Failed to send reset email');
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  resetUserPassword(): void {
    if (this.passwordResetForm.invalid || this.isLoading) return;

    this.isLoading = true;
    const resetCode = this.route.snapshot.queryParams['code'];
    const password = this.passwordResetForm.get('password')?.value;

    if (!resetCode || !password) {
      this.toastr.error('Invalid reset code or password');
      this.isLoading = false;
      return;
    }

    this.userService.resetPassword(resetCode, password).subscribe({
      next: () => {
        this.emailClicked = false;
        this.resetPassword = true;
        this.emailSent = false;
        this.resetSuccess = true;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.toastr.error(error.error.message || 'Failed to reset password');
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;

    if (password !== confirmPassword) {
      return { passwordMismatch: true };
    }
    return null;
  }
}
